import { toBlob, toPng } from 'dom-to-image';
import { Share2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import VestMarketsLogo from '@/assets/branding/vest-markets-logo';
import { Check, Copy } from '@/components/icons/barrel';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/components/ui/';
import config from '@/config';
import useReferrals from '@/features/referrals/api/get-referrals'; // eslint-disable-line import/no-restricted-paths
import { privateApi } from '@/lib/api-clients/rest-client';
import { cn } from '@/lib/utils';
import { getMarketStore } from '@/store/use-markets-store';
import { abbrFromSym, imgFromSym } from '@/utils/token-symbol';
import { bigIntToDecimalStr, formatNumber } from '@/utils/value-format';

const { isDevelopment } = config;
const subdomain = isDevelopment ? 'paper' : 'trade';

const getImageSrc = async (type: 'profit' | 'loss') => {
  try {
    if (type === 'profit') {
      const module = await import('@/assets/referral/profit.png');
      return module.default;
    } else {
      const module = await import('@/assets/referral/loss.png');
      return module.default;
    }
  } catch (error) {
    return '';
  }
};

const images = [
  {
    name: 'profit',
    getSrc: () => getImageSrc('profit'),
    className: 'right-0 bottom-0 top-0 h-full',
  },
  {
    name: 'loss',
    getSrc: () => getImageSrc('loss'),
    className: 'right-0 bottom-0 top-0 h-full',
  },
];

type SharedOrder = {
  symbol: string;
  roePct: string;
  pnl: string;
  isLong: boolean;
  leverage?: string;
  entryPrice: bigint;
  isFilled: boolean;
  lastFilledPrice?: bigint;
};

type Props = {
  onOpen(): SharedOrder;
};

export default function Share({ onOpen }: Props) {
  const { t } = useTranslation();
  const [isShareLoading, setIsShareLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const {
    data: referrals,
    isLoading: areReferralsLoading,
    isSuccess,
  } = useReferrals();
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number>();
  const imageRef = useRef<HTMLDivElement | null>(null);
  const [frozenOrder, setFrozenOrder] = useState<SharedOrder | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isImageCopying, setIsImageCopying] = useState<boolean>(false);
  const [copyModalPng, setCopyModalPng] = useState<string>('');
  const [loadedImages, setLoadedImages] = useState<{
    profit?: string;
    loss?: string;
  }>({});

  // const [stats, setStats] = useState<Partial<PositionColumn>>(position);

  // const roePct = frozenOrder.status === OrderStatus.NEW ? (frozenOrder as NewOrder).

  useEffect(() => {
    if (!copied) return;
    copyTimer.current = window.setTimeout(() => setCopied(false), 2000);
    return () => {
      copyTimer.current && window.clearTimeout(copyTimer.current);
    };
  }, [copied]);

  const onOpenChange = (open: boolean) => {
    setCopyModalPng('');
    if (open) {
      (async () => {
        const profitSrc = await getImageSrc('profit');
        const lossSrc = await getImageSrc('loss');
        setLoadedImages({ profit: profitSrc, loss: lossSrc });
      })();
      const order = onOpen();
      setFrozenOrder({
        ...order,
        lastFilledPrice:
          order.lastFilledPrice ??
          getMarketStore(order.symbol).getState().marketData.markPrice,
      });
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const getImageStyles = (scale: number = 2): any => {
    if (!imageRef.current) return;
    return {
      width: imageRef.current.clientWidth * scale,
      height: imageRef.current.clientHeight * scale,
      style: {
        width: `${imageRef.current.clientWidth}px`,
        height: `${imageRef.current.clientHeight}px`,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      },
    };
  };

  async function shareOnTwitter(symbol = 'ETH-PERP', refCode?: string) {
    if (!imageRef.current || !frozenOrder) return;
    const percentage = formatNumber(frozenOrder.roePct, {
      digits: 1,
      style: 'percent',
      showChange: false,
      round: 'ceil',
    }).replace('-', '');

    const refText = refCode
      ? frozenOrder.isFilled
        ? t('share.refCodeBoost')
        : t('share.refCodeEarn')
      : frozenOrder.isFilled
        ? '!'
        : '';

    const text = frozenOrder.isFilled
      ? t('share.twitterTextFilled', {
          action:
            Number(frozenOrder.pnl) > 0 ? t('share.made') : t('share.lost'),
          percentage,
          refText,
        })
      : t('share.twitterTextUnfilled', {
          symbol: abbrFromSym(symbol),
          refText,
        });
    if (referrals?.has_twitter_access) {
      setIsShareLoading(true);
      const promise = async function () {
        const base64 = await toPng(imageRef.current!, getImageStyles());
        await privateApi.post('/account/twitter/send', {
          text,
          base64,
        });
      };

      toast.promise(promise, {
        loading: t('share.sharingPost'),
        success: () => {
          setIsShareLoading(false);
          setIsOpen(false);
          return t('share.postShared');
        },
        error: (err) => {
          setIsShareLoading(false);
          console.error("Couldn't share the post: ", err);
          return t('share.shareError');
        },
      });
    } else {
      const url = refCode
        ? `https://${subdomain}.vestmarkets.com/join/${refCode}?symbol=${symbol}`
        : `https://${subdomain}.vestmarkets.com/trade/${symbol}`;
      const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url,
      )}&text=${encodeURIComponent(text)}`;
      const mobileTweetUrl = `twitter://post?message=${encodeURIComponent(url)}`;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.location.href = mobileTweetUrl;
        setTimeout(() => {
          window.open(tweetUrl, '_blank', 'noopener,noreferrer');
        }, 500);
      } else {
        window.open(tweetUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }

  const downloadImage = async () => {
    if (!imageRef.current || !frozenOrder) return;
    const png = await toPng(imageRef.current, getImageStyles());
    const link = document.createElement('a');
    link.href = png;
    link.download = `vest-${frozenOrder.symbol}-position.png`;
    link.click();
  };

  const handleDownloadImage = async () => {
    setIsImageLoading(true);

    toast.promise(downloadImage, {
      loading: t('share.downloadingImage'),
      success: () => t('share.imageDownloaded'),
      error: () => t('share.downloadImageError'),
      finally: () => setIsImageLoading(false),
    });
  };

  const copyImage = async (): Promise<void> => {
    if (!imageRef.current || !frozenOrder) return;

    const blob = await toBlob(imageRef.current, getImageStyles(3));

    if (!blob) return;

    if (
      typeof navigator.clipboard?.write === 'function' &&
      typeof window.ClipboardItem !== 'undefined'
    ) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);

        return;
      } catch {
        /* empty */
      }
    }

    try {
      if (navigator.canShare && window.File) {
        const file = new File(
          [blob],
          `vest-${frozenOrder.symbol}-position.png`,
          { type: 'image/png' },
        );

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file] });

          return;
        }
      }
    } catch {
      /* empty */
    }

    const png = await toPng(imageRef.current, getImageStyles());

    setCopyModalPng(png);

    throw new Error();
  };

  const handleCopyImage = async () => {
    setIsImageCopying(true);

    toast.promise(copyImage, {
      loading: t('share.copyingImage'),
      success: () => t('share.imageCopied'),
      error: () => t('share.copyImageError'),
      finally: () => setIsImageCopying(false),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger>
        <Share2
          size={14}
          className="text-muted-foreground hover:text-foreground"
        />
      </DialogTrigger>
      {frozenOrder && (
        <DialogContent className="w-full p-6 sm:w-full sm:max-w-lg md:max-w-xl lg:max-w-max lg:flex-row">
          <DialogHeader className="pb-4">
            {/* <DialogTitle className="text-3xl font-normal uppercase text-primary [text-shadow:_0_0_30px_rgb(255,90,68)]">
            share your trade
          </DialogTitle> */}
          </DialogHeader>
          {copyModalPng ? (
            <div className="flex flex-col gap-4">
              <img
                src={copyModalPng}
                className="border-b-[1px] max-md:w-full lg:h-[480px] lg:min-h-[480px]"
                alt="PnL"
              />

              <div className="m-4 text-center font-mono">
                {t('share.copyText')}
              </div>
            </div>
          ) : (
            <div
              className={cn(
                'flex flex-col gap-4',
                referrals?.ref_code && 'lg:flex-row lg:gap-8',
              )}
            >
              <div
                ref={imageRef}
                className="relative flex aspect-[4/3] flex-col justify-between gap-2 overflow-hidden border border-border bg-background p-6 max-md:w-full lg:h-[480px] lg:min-h-[480px] lg:flex-1"
              >
                <div className="flex w-32 items-center lg:w-60">
                  <VestMarketsLogo />
                </div>
                <div className="relative z-10 flex flex-col gap-2 lg:gap-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={imgFromSym(frozenOrder.symbol)}
                      alt="symbol"
                      className="h-8 w-8 rounded-full object-contain"
                    />
                    <h4 className="font-sans text-lg font-semibold sm:text-xl">
                      {abbrFromSym(frozenOrder.symbol)}
                    </h4>
                    <h5
                      className={cn(
                        frozenOrder.isLong ? 'text-green' : 'text-red',
                        'font-mono',
                      )}
                    >
                      {frozenOrder.isLong ? t('share.long') : t('share.short')}
                    </h5>
                    <div
                      className={cn(
                        'flex items-center justify-center rounded px-2 py-0.5',
                        frozenOrder.isLong
                          ? 'bg-green/10 text-green'
                          : 'bg-red/10 text-red',
                      )}
                    >
                      <p className="text-center font-mono">
                        {formatNumber(frozenOrder.leverage, {
                          maxDigits: 1,
                          style: 'leverage',
                        })}
                      </p>
                    </div>
                  </div>
                  <p
                    className={cn(
                      'block font-mono text-4xl tracking-tighter md:text-5xl lg:text-8xl',
                      Number(frozenOrder.pnl) < 0 ? 'text-red' : 'text-green',
                    )}
                  >
                    <span>
                      {formatNumber(frozenOrder.roePct, {
                        digits: 1,
                        style: 'percent',
                        showChange: true,
                        round: 'ceil',
                      })}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col gap-4 lg:gap-6">
                  <div className="flex items-center gap-4 lg:h-7 lg:gap-6">
                    <div className="flex flex-col">
                      <span className="font-mono text-vestgrey-600">
                        {t('share.entry')}
                      </span>{' '}
                      <span className="font-mono text-lg font-medium">
                        {bigIntToDecimalStr(
                          frozenOrder.entryPrice,
                          getMarketStore(frozenOrder.symbol).getState()
                            .marketSpec.priceDecimals,
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="font-mono text-vestgrey-600">
                        {frozenOrder.isFilled
                          ? t('share.close')
                          : t('share.current')}
                      </span>{' '}
                      <span className="font-mono text-lg font-medium">
                        {frozenOrder.lastFilledPrice !== undefined &&
                          bigIntToDecimalStr(
                            frozenOrder.lastFilledPrice,
                            getMarketStore(frozenOrder.symbol).getState()
                              .marketSpec.priceDecimals,
                          )}
                      </span>
                    </div>
                  </div>
                  {referrals?.ref_code && (
                    <div>
                      <p className="font-mono text-sm uppercase text-vestgrey-600 md:text-base">
                        {t('share.referralCode')}
                      </p>
                      <p className="mt-1 text-xl font-bold text-primary md:text-3xl">
                        {referrals.ref_code}
                      </p>
                    </div>
                  )}
                </div>
                {images.map(({ name, className }) => {
                  const imageSrc =
                    loadedImages[name as keyof typeof loadedImages];
                  if (!imageSrc) return null;

                  return (
                    <img
                      className={cn(
                        'absolute object-contain',
                        className,
                        (
                          Number(frozenOrder.pnl) < 0
                            ? name === 'loss'
                            : name === 'profit'
                        )
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                      src={imageSrc}
                      alt=""
                      key={`glow-${name}`}
                    />
                  );
                })}
              </div>

              <div className="flex flex-col justify-between gap-4">
                <div className="flex min-w-96 flex-col gap-4">
                  {!areReferralsLoading && isSuccess && referrals.ref_code && (
                    <div className="mb-2 hidden flex-col items-center gap-3 lg:flex lg:items-start">
                      <h3 className="text-xl">{t('share.yourReferralCode')}</h3>
                      <div className="flex items-center gap-3">
                        <span className="text-font text-5xl font-bold uppercase">
                          {referrals.ref_code}
                        </span>
                        {referrals.ref_code && (
                          <Button
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                `https://trade.vest.exchange/join/${referrals.ref_code}?symbol=${frozenOrder.symbol}`,
                              );
                              setCopied(true);
                            }}
                            className="h-10 w-10 rounded-none bg-background font-mono uppercase text-foreground hover:bg-background hover:text-foreground"
                          >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={cn(
                    'grid',
                    !referrals?.ref_code && 'lg:grid-cols-3',
                  )}
                >
                  <Button
                    type="button"
                    disabled={isShareLoading}
                    onClick={() =>
                      shareOnTwitter(frozenOrder.symbol, referrals?.ref_code)
                    }
                    className="h-14 rounded-none font-mono text-lg uppercase tracking-wider"
                    size="lg"
                  >
                    {isShareLoading
                      ? t('share.sharingOnX')
                      : t('share.shareOnX')}
                  </Button>

                  <Button
                    variant="secondary"
                    type="button"
                    disabled={isImageLoading}
                    onClick={handleDownloadImage}
                    className="h-14 rounded-none font-mono text-lg uppercase disabled:opacity-50"
                  >
                    {t('share.downloadImage')}
                  </Button>

                  <Button
                    variant="secondary"
                    type="button"
                    className="h-14 rounded-none font-mono text-lg uppercase disabled:opacity-50"
                    disabled={isImageCopying}
                    onClick={handleCopyImage}
                  >
                    {t('share.copyImage')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
